import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiSearch, FiShield } from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { PermissionMatrixEditor } from '../../components/PermissionMatrixEditor';
import {
    alternarCelula,
    chavesDeExcecao,
    limparExcecoesRedundantes,
    valorEfetivo,
} from '../../components/PermissionMatrixEditor/excecoes';
import { accessProfilesApi, adminUsersApi, type AdminUserEmpresaPermissions } from '../../services/admin';
import { PERMISSION_MODULE_ACTIONS, type AccessProfile, type PermissionAction, type PermissionItem, type PermissionModule } from '../../types';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';

interface UserRow {
    id: string;
    name: string;
    email: string;
    role: string;
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
}

type FilterStatus = 'all' | 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

const ALL_MODULES = Object.keys(PERMISSION_MODULE_ACTIONS) as PermissionModule[];
/**
 * Acesso do usuario numa empresa: um perfil e uma grade de excecoes que valem
 * para todas as filiais dele ali (o acesso e por usuario, nao por filial).
 *
 * A grade e a mesma da tela de Perfis de Acesso e mostra o que o usuario pode
 * de fato. Clicar numa celula cria uma excecao quando o valor passa a diferir
 * do perfil, e a apaga quando volta a ele; a celula destacada e sempre uma
 * diferenca real em relacao ao perfil.
 */
export function EmpresaPermissionsCard({
    empresa,
    profiles,
    onSave,
    filiaisDisponiveis,
}: {
    empresa: AdminUserEmpresaPermissions;
    profiles: AccessProfile[];
    onSave: (
        empresaId: string,
        data: { accessProfileId?: string; overrides: PermissionItem[]; filialIds?: string[] },
    ) => Promise<unknown>;
    /** Negócios da empresa. Quando informado (tela do dono), dá para escolher quais o usuário vê. */
    filiaisDisponiveis?: { id: string; name: string }[];
}) {
    const [accessProfileId, setAccessProfileId] = useState(empresa.accessProfile?.id ?? '');
    const [excecoes, setExcecoes] = useState<PermissionItem[]>(empresa.overrides);
    const [filialIds, setFilialIds] = useState<string[]>(empresa.filiais.map((f) => f.id));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState(false);

    // Perfil atribuido que nao esta na lista (ex.: perfil de empresa visto pela
    // equipe LTQ) continua aparecendo, para o select nao parecer vazio.
    const opcoesDePerfil = [
        ...profiles.map((p) => ({ value: p.id, label: p.name })),
        ...(empresa.accessProfile && !profiles.some((p) => p.id === empresa.accessProfile!.id)
            ? [{ value: empresa.accessProfile.id, label: `${empresa.accessProfile.name} (da empresa)` }]
            : []),
    ];

    if (empresa.souDono) {
        return (
            <div className={styles.detailCard} style={{ marginBottom: 16 }}>
                <h3>{empresa.empresaName}</h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--color-gray-500)' }}>
                    Dono da empresa: tem acesso total a todos os módulos e negócios dela. Não há o que ajustar aqui.
                </p>
            </div>
        );
    }

    const alternarFilial = (id: string) => {
        setFilialIds((atuais) => (atuais.includes(id) ? atuais.filter((f) => f !== id) : [...atuais, id]));
        setDirty(true);
    };

    const perfil = profiles.find((p) => p.id === accessProfileId)?.permissions ?? [];

    const efetivas: PermissionItem[] = ALL_MODULES.flatMap((module) =>
        PERMISSION_MODULE_ACTIONS[module].map((action) => ({ module, action, allowed: valorEfetivo(perfil, excecoes, module, action) })),
    );

    const trocarPerfil = (id: string) => {
        setAccessProfileId(id);
        const novoPerfil = profiles.find((p) => p.id === id)?.permissions ?? [];
        setExcecoes((atuais) => limparExcecoesRedundantes(novoPerfil, atuais));
        setDirty(true);
    };

    const alternar = (module: PermissionModule, action: PermissionAction) => {
        setExcecoes((atuais) => alternarCelula(perfil, atuais, module, action));
        setDirty(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        if (filiaisDisponiveis && filialIds.length === 0) {
            setError('Escolha ao menos um negócio que este usuário vai ver.');
            setSaving(false);
            return;
        }
        try {
            await onSave(empresa.empresaId, {
                accessProfileId: accessProfileId || undefined,
                overrides: excecoes,
                ...(filiaisDisponiveis ? { filialIds } : {}),
            });
            setDirty(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao salvar os acessos');
        } finally {
            setSaving(false);
        }
    };

    const nomesDasFiliais = empresa.filiais.map((f) => f.name).join(', ');

    return (
        <div className={styles.detailCard} style={{ marginBottom: 16 }}>
            <h3>{empresa.empresaName}</h3>
            {filiaisDisponiveis ? (
                <div style={{ margin: '4px 0 16px' }}>
                    <label className={styles.formLabel}>Negócios que este usuário vê</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                        {filiaisDisponiveis.map((f) => (
                            <label key={f.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                                <input
                                    type="checkbox"
                                    checked={filialIds.includes(f.id)}
                                    onChange={() => alternarFilial(f.id)}
                                    aria-label={f.name}
                                />
                                {f.name}
                            </label>
                        ))}
                    </div>
                </div>
            ) : (
                <p style={{ margin: '4px 0 16px', fontSize: 13, color: 'var(--color-gray-500)' }}>
                    {empresa.filiais.length > 1 ? `Vale para os ${empresa.filiais.length} negócios: ` : 'Vale para o negócio: '}
                    <strong style={{ color: 'var(--color-gray-700)' }}>{nomesDasFiliais}</strong>
                </p>
            )}

            {empresa.divergente && (
                <div
                    role="status"
                    style={{ marginBottom: 16, padding: '10px 12px', borderRadius: 8, background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e', fontSize: 13 }}
                >
                    Este usuário tinha acessos diferentes em cada negócio. Abaixo está o da {empresa.filiais[0]?.name}; ao
                    salvar, ele passa a valer para todos.
                </div>
            )}

            <div className={styles.formGroup} style={{ maxWidth: 360, marginBottom: 16 }}>
                <SearchableSelect
                    label="Perfil de Acesso"
                    value={accessProfileId}
                    onChange={trocarPerfil}
                    options={opcoesDePerfil}
                    placeholder="Selecione um perfil..."
                    dropdownVariant="inline"
                />
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <label className={styles.formLabel} style={{ margin: 0 }}>O que este usuário pode fazer</label>
                <span style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>
                    Marcado vem do perfil. Mude uma célula para criar uma exceção{' '}
                    <span style={{ padding: '1px 6px', borderRadius: 4, background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e' }}>
                        assim
                    </span>
                    .
                </span>
            </div>
            <PermissionMatrixEditor value={efetivas} destaques={chavesDeExcecao(excecoes)} onToggle={alternar} />

            {error && <p className={styles.formError}>{error}</p>}
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--color-gray-500)' }}>
                    {excecoes.length === 0 ? 'Sem exceções: vale exatamente o perfil.' : `${excecoes.length} exceção(ões) em relação ao perfil.`}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={excecoes.length === 0 || saving}
                        onClick={() => { setExcecoes([]); setDirty(true); }}
                    >
                        Remover exceções
                    </Button>
                    <Button type="button" disabled={!dirty || saving} onClick={handleSave}>
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export function AdminUsers() {
    const [users, setUsers] = useState<UserRow[]>([]);
    const [profiles, setProfiles] = useState<AccessProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
    const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
    const [empresas, setEmpresas] = useState<AdminUserEmpresaPermissions[] | null>(null);
    const [loadingVinculos, setLoadingVinculos] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [usersData, profilesData] = await Promise.all([
                adminUsersApi.list({ status: filterStatus !== 'all' ? filterStatus : undefined }),
                accessProfilesApi.list(),
            ]);
            setUsers(usersData);
            setProfiles(profilesData);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const filteredUsers = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return users;
        return users.filter((u) => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term));
    }, [users, searchTerm]);

    const openUser = async (user: UserRow) => {
        setSelectedUser(user);
        setEmpresas(null);
        setLoadingVinculos(true);
        try {
            setEmpresas(await adminUsersApi.getPermissions(user.id));
        } catch (e) {
            console.error(e);
            setEmpresas([]);
        } finally {
            setLoadingVinculos(false);
        }
    };

    const handleSaveEmpresa = async (empresaId: string, data: { accessProfileId?: string; overrides: PermissionItem[] }) => {
        if (!selectedUser) return;
        const resultado = await adminUsersApi.updatePermissions(selectedUser.id, empresaId, data);
        // Recarrega para refletir o que ficou gravado (e apagar o aviso de divergencia).
        setEmpresas(await adminUsersApi.getPermissions(selectedUser.id));
        return resultado;
    };

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Usuários</h2>
                    <p>Gerencie o perfil de acesso e as exceções de cada usuário. Valem para todos os negócios dele.</p>
                </div>
            </div>

            <div className={styles.filtersRow}>
                <div className={styles.searchInput}>
                    <FiSearch size={16} className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Buscar por nome ou e-mail..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className={styles.filterButtons}>
                    {(['all', 'ACTIVE', 'INACTIVE', 'SUSPENDED'] as FilterStatus[]).map((s) => (
                        <button
                            key={s}
                            className={`${styles.filterBtn} ${filterStatus === s ? styles.active : ''}`}
                            onClick={() => setFilterStatus(s)}
                        >
                            {s === 'all' ? 'Todos' : s === 'ACTIVE' ? 'Ativos' : s === 'INACTIVE' ? 'Inativos' : 'Suspensos'}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className={styles.loadingState}>Carregando...</div>
            ) : (
                <div className={styles.usersSection}>
                    <table className={styles.usersTable}>
                        <thead>
                            <tr>
                                <th>Usuário</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((u) => (
                                <tr key={u.id}>
                                    <td>
                                        <div className={styles.userInfo}>
                                            <div className={styles.userAvatar}>{u.name.slice(0, 2).toUpperCase()}</div>
                                            <div className={styles.userDetails}>
                                                <div className={styles.name}>{u.name}</div>
                                                <div className={styles.email}>{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${styles[u.status.toLowerCase()]}`}>
                                            {u.status === 'ACTIVE' ? 'Ativo' : u.status === 'SUSPENDED' ? 'Suspenso' : 'Inativo'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.actionButtons}>
                                            <button className={styles.actionBtn} onClick={() => openUser(u)} title="Gerenciar acessos">
                                                <FiShield size={14} /> Acessos
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredUsers.length === 0 && (
                                <tr><td colSpan={3} className={styles.emptyRow}>Nenhum usuário encontrado.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                isOpen={!!selectedUser}
                onClose={() => { setSelectedUser(null); setEmpresas(null); }}
                title={selectedUser ? `Acessos de ${selectedUser.name}` : ''}
                size="xlarge"
                scrollBody
            >
                {loadingVinculos && <div className={styles.loadingState}>Carregando...</div>}
                {!loadingVinculos && empresas?.length === 0 && (
                    <p>Este usuário não está vinculado a nenhum negócio ainda.</p>
                )}
                {!loadingVinculos && empresas?.map((e) => (
                    <EmpresaPermissionsCard key={e.empresaId} empresa={e} profiles={profiles} onSave={handleSaveEmpresa} />
                ))}
            </Modal>
        </AdminLayout>
    );
}
